import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
} from 'react';
import { connect } from 'react-redux';
import { useCombobox } from 'downshift';
import classNames from 'classnames';
import { FormFeedback } from 'reactstrap';
import { Icon } from 'js/components';
import { useQuery } from 'js/hooks/useQuery';
import { listJiraBoards } from 'js/actions/jira-connector-actions';
import { useStandardCollator, isNullOrWhitespace } from 'js/utils/string';

import styles from './JiraBoardSelector.module.scss';

const BOARD_FIELD_NAME = 'board_id';

function boardToLabel(board) {
  if (!board) return '';
  const key = board.location?.projectKey ?? '';
  return key ? `${key} - ${board.name}` : board.name;
}

function metadataToLabel({ projectKey, projectName, boardName }) {
  return (
    [
      projectKey?.trim() || projectName?.trim(),
      boardName?.trim() || projectName?.trim(),
    ]
      .filter((s) => Boolean(s))
      .join(' - ') ?? ''
  );
}

function buildBoardTokens(board) {
  const name = (board.name || '').toLowerCase();
  const projectKey = (board.location?.projectKey || '').toLowerCase();
  const projectName = (board.location?.projectName || '').toLowerCase();
  const parts = [name, projectKey, projectName].filter(Boolean);
  return parts.flatMap((s) => s.split(/\W+/).filter(Boolean));
}

function JiraBoardSelector({
  value = '',
  onChange,
  onBlur,
  error = null,
  touched = false,
  workspaceId,
  connectorId,
  persistedProjectKey = '',
  persistedProjectName = '',
  persistedBoardName = '',
  onSelectedBoardChange = () => {},
  listJiraBoards,
}) {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const userTypedThisSessionRef = useRef(false);
  const cursorRef = useRef(null);
  const [cursor, setCursor] = useState(null);

  const refreshJiraBoardsQuery = React.useCallback(
    (queryId) => {
      if (workspaceId && connectorId) {
        return listJiraBoards(connectorId, queryId);
      }
      return Promise.resolve(null);
    },
    [workspaceId, connectorId, listJiraBoards]
  );

  const {
    result: jiraBoards,
    refresh: refreshJiraBoards,
    isLoading: isLoadingJiraBoards,
    hasLoaded: hasLoadedJiraBoards,
  } = useQuery({
    queryId: `jira-boards-${workspaceId}-${connectorId}`,
    query: refreshJiraBoardsQuery,
  });

  useEffect(() => {
    if (workspaceId && connectorId) {
      refreshJiraBoards();
    }
  }, [workspaceId, connectorId, refreshJiraBoards]);

  const collator = useStandardCollator();
  const rawBoards = useMemo(() => {
    const values = jiraBoards?.values ?? [];
    return values.filter(
      (board) => !isNullOrWhitespace(board.location?.projectKey)
    );
  }, [jiraBoards?.values]);
  const boardsWithTokens = useMemo(() => {
    const withTokens = rawBoards.map((board) => ({
      ...board,
      tokens: buildBoardTokens(board),
    }));
    return withTokens.slice().sort((a, b) => {
      const keyCmp = collator.compare(
        a.location?.projectKey ?? '',
        b.location?.projectKey ?? ''
      );
      if (keyCmp !== 0) return keyCmp;

      const nameCmp = collator.compare(
        a.location?.projectName ?? '',
        b.location?.projectName ?? ''
      );
      if (nameCmp !== 0) return nameCmp;

      const boardCmp = collator.compare(a.name ?? '', b.name ?? '');
      if (boardCmp !== 0) return boardCmp;

      return a.id > b.id ? 1 : a.id < b.id ? -1 : 0;
    });
  }, [rawBoards, collator]);

  const filteredBoards = useMemo(() => {
    if (!userTypedThisSessionRef.current || !inputValue || !inputValue.trim()) {
      return boardsWithTokens;
    }
    const searchTokens = inputValue
      .split(/\W+/)
      .map((t) => t.toLowerCase())
      .filter(Boolean);
    return boardsWithTokens.filter((board) =>
      searchTokens.every((token) => board.tokens.some((t) => t.includes(token)))
    );
  }, [boardsWithTokens, inputValue]);

  const selectedBoard = useMemo(
    () => rawBoards.find((b) => String(b.id) === String(value)) || null,
    [rawBoards, value]
  );

  const selectedDisplayLabel = useMemo(
    () =>
      selectedBoard
        ? boardToLabel(selectedBoard)
        : metadataToLabel({
            persistedProjectKey,
            persistedProjectName,
            persistedBoardName,
          }),
    [
      selectedBoard,
      persistedProjectKey,
      persistedProjectName,
      persistedBoardName,
    ]
  );

  const isDisabled = isLoadingJiraBoards && !hasLoadedJiraBoards;

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items: filteredBoards,
    selectedItem: selectedBoard,
    inputValue: isFocused ? inputValue : selectedDisplayLabel || inputValue,
    itemToString: boardToLabel,
    onInputValueChange: ({ inputValue: next }) => {
      userTypedThisSessionRef.current = true;
      setInputValue(next ?? '');
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem && onChange) {
        onChange({
          target: {
            name: BOARD_FIELD_NAME,
            id: BOARD_FIELD_NAME,
            value: String(selectedItem.id),
          },
        });
      }

      if (selectedItem) {
        onSelectedBoardChange(selectedItem);
      }
    },
    onStateChange: ({ type, selectedItem }) => {
      if (
        type === useCombobox.stateChangeTypes.ItemClick &&
        selectedItem &&
        onChange
      ) {
        onChange({
          target: {
            name: BOARD_FIELD_NAME,
            id: BOARD_FIELD_NAME,
            value: String(selectedItem.id),
          },
        });
        setInputValue('');
      }

      if (type === useCombobox.stateChangeTypes.ItemClick && selectedItem) {
        onSelectedBoardChange(selectedItem);
      }
    },
  });

  const downshiftInputProps = getInputProps({
    id: BOARD_FIELD_NAME,
    name: BOARD_FIELD_NAME,
    disabled: isDisabled,
    placeholder: isLoadingJiraBoards
      ? 'Loading boards...'
      : 'Select or type to search boards...',
    ref: cursorRef,
  });

  // When focused, always show what the user typed (including empty). When not focused, show selected label.
  const displayValue = isFocused ? inputValue : selectedDisplayLabel;

  useLayoutEffect(() => {
    const input = cursorRef.current;
    if (input && cursor != null) {
      input.setSelectionRange(cursor, cursor);
    }
  }, [cursor, displayValue]);

  const inputProps = {
    ...downshiftInputProps,
    value: displayValue,
    onChange: (e) => {
      setCursor(e.target.selectionStart);
      downshiftInputProps.onChange?.(e);
    },
    onFocus: (e) => {
      userTypedThisSessionRef.current = false;
      setIsFocused(true);
      setInputValue(selectedDisplayLabel);
      downshiftInputProps.onFocus?.(e);
    },
    onBlur: (e) => {
      setIsFocused(false);
      if (
        !inputValue.trim() &&
        value &&
        userTypedThisSessionRef.current &&
        onChange
      ) {
        onChange({
          target: { name: BOARD_FIELD_NAME, id: BOARD_FIELD_NAME, value: '' },
        });
        onSelectedBoardChange(null);
      }
      setInputValue('');
      if (onBlur) onBlur({ target: { name: BOARD_FIELD_NAME } });
      downshiftInputProps.onBlur?.(e);
    },
  };

  const toggleProps = getToggleButtonProps({ disabled: isDisabled });
  const hasError = error && touched;

  return (
    <>
      <div className={classNames(styles.root, 'combobox')}>
        <div className="input-group limited-width-lg">
          <input
            placeholder={
              isLoadingJiraBoards
                ? 'Loading boards...'
                : 'Select or type to search boards...'
            }
            className={classNames('form-control', {
              'is-invalid': hasError,
            })}
            aria-invalid={hasError}
            aria-required
            {...inputProps}
          />
          <div className="input-group-append">
            <span aria-label="toggle menu" type="button" {...toggleProps}>
              {isOpen ? (
                <Icon icon={['far', 'chevron-up']} fixedWidth />
              ) : (
                <Icon icon={['far', 'chevron-down']} fixedWidth />
              )}
            </span>
          </div>
        </div>
        <ul
          {...getMenuProps()}
          className="combobox-items"
          style={!isOpen ? { display: 'none' } : {}}
        >
          {isOpen &&
            (filteredBoards.length === 0 ? (
              <li className="combobox-item" key="no-results">
                {inputValue.trim()
                  ? 'No matching boards'
                  : 'No boards available'}
              </li>
            ) : (
              filteredBoards.map((board, index) => (
                <li
                  className={classNames({
                    highlighted: highlightedIndex === index,
                    'combobox-item': true,
                  })}
                  key={board.id}
                  {...getItemProps({ item: board, index })}
                >
                  <div className={styles.label}>
                    {board.location?.projectKey} - {board.location?.projectName}
                  </div>
                  <div className={styles.name}>{board.name}</div>
                </li>
              ))
            ))}
        </ul>
      </div>
      {hasError && <FormFeedback>{error}</FormFeedback>}
    </>
  );
}

export default connect(null, { listJiraBoards })(JiraBoardSelector);
